import React, { useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import LabLayout from '../../components/LabLayout';
import AuthService from '../../services/authService';
import useLabJobs from '../../utils/useLabJobs';
import {
  LAB_FLAG_META,
  LabAvatar,
  LabFlagChip,
  LabJobThread,
  LabStatusBadge,
  labFullDate,
  labTimeAgo,
} from '../../components/LabJobParts';

const TABS = [
  { key: 'queue', label: 'Queue' },
  { key: 'on hold', label: 'On hold' },
  { key: 'solved', label: 'Solved' },
];

function StatCard({ label, value, tone }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${tone}`}>{value}</p>
    </div>
  );
}

// Asks for an optional note; resolves to the note text, or null if cancelled.
async function askNote({ title, placeholder, confirmText, confirmColor }) {
  const result = await Swal.fire({
    title,
    input: 'textarea',
    inputPlaceholder: placeholder,
    inputAttributes: { maxlength: 2000 },
    showCancelButton: true,
    confirmButtonText: confirmText,
    confirmButtonColor: confirmColor,
  });
  return result.isConfirmed ? String(result.value || '').trim() : null;
}

function JobCard({ job, viewer, canWork, nextInLine, actions }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const flagMeta = LAB_FLAG_META[job.flag] || LAB_FLAG_META.normal;
  const veryImportant = job.flag === 'very_important';
  const pending = job.status === 'pending';
  // Same rule the server enforces: head of the line, or a very important job.
  const canPick = canWork && pending && (viewer.isManager || job.queuePosition === 1 || veryImportant);

  const guard = (fn) => async () => {
    if (busy) return;
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  const start = guard(() => actions.changeStatus(job.jobId, 'in process'));
  const hold = guard(async () => {
    const note = await askNote({ title: 'Put this job on hold?', placeholder: 'Why is it on hold? (optional)', confirmText: 'Put on hold', confirmColor: '#7c3aed' });
    if (note !== null) await actions.changeStatus(job.jobId, 'on hold', note);
  });
  const resume = guard(() => actions.changeStatus(job.jobId, 'in process'));
  const solve = guard(async () => {
    const note = await askNote({ title: 'Mark this job as solved?', placeholder: 'What was done? (optional, the requester will see it)', confirmText: 'Mark solved', confirmColor: '#059669' });
    if (note !== null) await actions.changeStatus(job.jobId, 'solved', note);
  });
  const requeue = guard(async () => {
    const confirm = await Swal.fire({
      title: 'Put back in the queue?',
      text: 'It returns to its original place in the line.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Put back',
    });
    if (confirm.isConfirmed) await actions.changeStatus(job.jobId, 'pending');
  });
  const reopen = guard(async () => {
    const confirm = await Swal.fire({
      title: 'Reopen this job?',
      text: 'It goes back to Pending at its original place in the queue.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Reopen',
    });
    if (confirm.isConfirmed) await actions.changeStatus(job.jobId, 'pending');
  });

  const btn = 'rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <div className={`overflow-hidden rounded-xl border border-gray-200 border-l-4 bg-white shadow-sm ${flagMeta.stripe} ${veryImportant && pending ? 'ring-2 ring-red-200' : ''}`}>
      <div className="flex items-start gap-3 px-4 py-4">
        {pending && (
          <span
            className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${job.queuePosition === 1 ? 'bg-teal-600 text-white' : 'bg-amber-100 text-amber-800'}`}
            title="Position in the queue"
          >
            {job.queuePosition}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-base font-semibold text-gray-900">{job.title}</span>
            <LabFlagChip flag={job.flag} />
            {!pending && <LabStatusBadge status={job.status} />}
            {pending && job.queuePosition === 1 && <span className="rounded-full bg-teal-100 px-2.5 py-0.5 text-xs font-bold text-teal-800">Next up</span>}
          </div>
          {job.description && <p className="mt-1.5 whitespace-pre-wrap break-words text-sm text-gray-700">{job.description}</p>}
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <LabAvatar person={job.creator} size="h-5 w-5" textSize="text-[10px]" />
              {job.creator?.name || 'Someone'}
            </span>
            <span title={labFullDate(job.createdAt)}>Requested {labTimeAgo(job.createdAt)}</span>
            {job.location && <span>Place: <span className="font-semibold text-gray-700">{job.location}</span></span>}
            {job.startedAt && job.status !== 'pending' && <span title={labFullDate(job.startedAt)}>Started {labTimeAgo(job.startedAt)}</span>}
            {job.status === 'solved' && job.solvedAt && <span className="text-emerald-700" title={labFullDate(job.solvedAt)}>Solved {labTimeAgo(job.solvedAt)}</span>}
            {job.handler && job.status !== 'pending' && <span>Handled by <span className="font-semibold text-gray-700">{job.handler.name}</span></span>}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 bg-gray-50/70 px-4 py-2.5">
        {pending && canWork && (
          <button
            type="button"
            onClick={start}
            disabled={!canPick || busy}
            title={canPick ? '' : `Jobs are taken in order — "${nextInLine?.title || 'the job ahead'}" comes first`}
            className={`${btn} ${veryImportant ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-teal-600 text-white hover:bg-teal-700'}`}
          >
            {canPick ? (veryImportant && job.queuePosition !== 1 ? 'Take now (very important)' : 'Start working') : `Waiting for #${(job.queuePosition || 2) - 1}`}
          </button>
        )}
        {pending && canWork && canPick && (
          <button type="button" onClick={solve} disabled={busy} className={`${btn} border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50`}>
            Already done
          </button>
        )}
        {job.status === 'in process' && canWork && (
          <>
            <button type="button" onClick={solve} disabled={busy} className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}>Mark solved</button>
            <button type="button" onClick={hold} disabled={busy} className={`${btn} border border-violet-300 bg-white text-violet-700 hover:bg-violet-50`}>Put on hold</button>
            <button type="button" onClick={requeue} disabled={busy} className={`${btn} border border-gray-300 bg-white text-gray-600 hover:bg-gray-50`}>Back to queue</button>
          </>
        )}
        {job.status === 'on hold' && canWork && (
          <>
            <button type="button" onClick={resume} disabled={busy} className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}>Resume</button>
            <button type="button" onClick={solve} disabled={busy} className={`${btn} border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50`}>Mark solved</button>
            <button type="button" onClick={requeue} disabled={busy} className={`${btn} border border-gray-300 bg-white text-gray-600 hover:bg-gray-50`}>Back to queue</button>
          </>
        )}
        {job.status === 'solved' && canWork && (
          <button type="button" onClick={reopen} disabled={busy} className={`${btn} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}>Reopen</button>
        )}
        <button type="button" onClick={() => setOpen((v) => !v)} className="ml-auto text-xs font-semibold text-teal-700 hover:text-teal-900">
          {open ? 'Hide updates' : `Updates${job.comments.length ? ` (${job.comments.length})` : ''}`}
        </button>
      </div>

      {open && (
        <div className="border-t border-gray-100 px-5 py-3">
          <LabJobThread
            job={job}
            currentStaffId={viewer.currentStaffId}
            isSuperAdmin={viewer.isSuperAdmin}
            onComment={actions.addComment}
            onDeleteComment={actions.deleteComment}
            composerPlaceholder="Post a progress update…  (Enter to post)"
          />
        </div>
      )}
    </div>
  );
}

export default function LabDashboard() {
  const lab = useLabJobs({ pollMs: 15000 });
  const { jobs, viewer, loading, loadError } = lab;
  const [tab, setTab] = useState('queue');
  const firstName = (AuthService.getUser()?.superAdmin?.staff?.name || '').split(' ')[0];

  const groups = useMemo(() => {
    const byUpdated = (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt);
    return {
      pending: jobs.filter((j) => j.status === 'pending').sort((a, b) => a.queuePosition - b.queuePosition),
      inProcess: jobs.filter((j) => j.status === 'in process').sort(byUpdated),
      onHold: jobs.filter((j) => j.status === 'on hold').sort(byUpdated),
      solved: jobs.filter((j) => j.status === 'solved').sort((a, b) => new Date(b.solvedAt) - new Date(a.solvedAt)),
    };
  }, [jobs]);

  const canWork = viewer.isLabAssistant || viewer.isManager;
  const urgentCount = groups.pending.filter((j) => j.flag === 'very_important').length;
  const actions = {
    changeStatus: lab.changeStatus,
    addComment: lab.addComment,
    deleteComment: lab.deleteComment,
  };
  const head = groups.pending[0];

  const renderList = (list, emptyText) => (list.length === 0
    ? <div className="rounded-xl border border-dashed border-gray-300 bg-white py-10 text-center text-sm text-gray-500">{emptyText}</div>
    : <div className="space-y-3">{list.map((job) => (
      <JobCard key={job.jobId} job={job} viewer={viewer} canWork={canWork} nextInLine={head} actions={actions} />
    ))}</div>);

  return (
    <LabLayout title="Dashboard">
      <div className="mx-auto max-w-5xl space-y-5">
        <div className="rounded-2xl bg-gradient-to-r from-teal-600 to-emerald-600 px-6 py-5 text-white shadow-sm">
          <h1 className="text-xl font-bold">Hello{firstName ? `, ${firstName}` : ''}</h1>
          <p className="mt-1 text-sm text-teal-50">
            {groups.pending.length
              ? `${groups.pending.length} job${groups.pending.length === 1 ? '' : 's'} waiting in the queue${head ? ` — next up: "${head.title}"` : ''}.`
              : 'The queue is empty. Nice work.'}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard label="In queue" value={groups.pending.length} tone="text-amber-600" />
          <StatCard label="In process" value={groups.inProcess.length} tone="text-blue-600" />
          <StatCard label="On hold" value={groups.onHold.length} tone="text-violet-600" />
          <StatCard label="Solved (30 days)" value={groups.solved.length} tone="text-emerald-600" />
        </div>

        {urgentCount > 0 && (
          <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <span className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-red-600" />
            <span>
              <strong>{urgentCount} very important job{urgentCount === 1 ? '' : 's'}</strong> in the queue — you can take {urgentCount === 1 ? 'it' : 'them'} right away, even out of turn.
            </span>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => {
            const count = t.key === 'queue' ? groups.pending.length + groups.inProcess.length : t.key === 'on hold' ? groups.onHold.length : groups.solved.length;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${tab === t.key ? 'bg-teal-600 text-white shadow-sm' : 'bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50'}`}
              >
                {t.label} <span className={`ml-1 text-xs ${tab === t.key ? 'text-teal-100' : 'text-gray-400'}`}>{count}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <p className="py-10 text-center text-sm text-gray-400">Loading…</p>
        ) : loadError ? (
          <p className="py-10 text-center text-sm text-red-600">{loadError}</p>
        ) : tab === 'queue' ? (
          <div className="space-y-6">
            {groups.inProcess.length > 0 && (
              <section>
                <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-blue-700">Working on ({groups.inProcess.length})</h2>
                {renderList(groups.inProcess, '')}
              </section>
            )}
            <section>
              <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-amber-700">Up next ({groups.pending.length})</h2>
              {renderList(groups.pending, 'Nothing is waiting. New jobs will appear here automatically.')}
            </section>
          </div>
        ) : tab === 'on hold' ? (
          renderList(groups.onHold, 'No jobs are on hold.')
        ) : (
          renderList(groups.solved, 'No jobs solved in the last 30 days.')
        )}
      </div>
    </LabLayout>
  );
}
